<svg class="logo logo--login" viewBox="0 0 111 39.96">
  <use xlink:href="#ab-logo"></use>
</svg>

<div class="form form--setup-mfa">
  <h1 class="sr-only">Setup Multi-Factor Authentication</h1>

  <ng-container *ngIf="orgRequired && currentStepKey === 'select-method'">
    <div class="alert alert--danger mb-0100">
      <h5 class="alert-color flex flexWrap-nowrap flexAlign--center">
        <i class="fas fa-user-lock mr-0025 txt-18"></i>
        <span class="pl-0025 txt-16">2-Factor Authentication Required</span>
      </h5>
      <div class="alert__content mt-0050 txt-14">
        <p>Your Organization requires an additional form of authentication to be configured.</p>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="errorMessages.length">
    <div *ngFor="let msg of errorMessages" class="alert alert--danger mb-0100">
      <div class="alert__content txt-14">
        <p>{{ msg }}</p>
      </div>
    </div>
  </ng-container>

  <form [formGroup]="currentForm" (ngSubmit)="onFormSubmit()" class="mb-0200">
    <!-- Step 1: Method Selection -->
    <div *ngIf="currentStepKey === 'select-method'" class="mb-0150 auth-card mat-card--simple mat-card--border">
      <div class="mb-0100">
        <h2 class="mb-0025">Setup 2-Factor Authentication</h2>
        <p class="txt-14">
          Choose an additional verification method below.
        </p>
      </div>

      <mat-radio-group formControlName="mfaType">
        <!-- TOTP -->
        <div class="mb-0100">
          <label class="mfa-verification-method"
                 [class.active]="currentForm.get('mfaType')?.value === mfaMethod.Totp">
            <mat-radio-button [value]="mfaMethod.Totp"></mat-radio-button>
            <i class="fal fa-fw fa-mobile-screen-button mr-0050"></i>
            <span>
              <span class="d-flex flexAlign--center">
                <span>Authenticator App</span>
                <span class="badge badge--green ml-0050">Recommended</span>
              </span>
              <small>Google Authenticator, 1Password, etc.</small>
            </span>
          </label>
        </div>

        <!-- SMS -->
        <div>
          <label class="mfa-verification-method"
                 [class.active]="currentForm.get('mfaType')?.value === mfaMethod.Sms">
            <mat-radio-button [value]="mfaMethod.Sms"></mat-radio-button>
            <i class="fal fa-fw fa-message-sms mr-0050"></i>
            <span>
              SMS
              <small>Receive a text message.</small>
            </span>
          </label>
        </div>
      </mat-radio-group>
    </div>

    <!-- Step 2: TOTP setup -->
    <div *ngIf="currentStepKey === 'setup-totp'" class="mb-0150 auth-card mat-card--simple mat-card--border">
      <div class="auth-card__header">
        <h2 class="auth-card__title">Setup 2-Factor Authentication</h2>
        <p class="auth-card__subtitle">Use an authenticator app such as 1Password, Google Authenticator or Authy and scan the QR code below.</p>
      </div>

      <div class="auth-card__content">
        <!-- QR Code -->
        <div class="mfa-setup-qr-code mb-0075">
          <div *ngIf="qrCodeSvg; else qrPlaceholder" [innerHTML]="qrCodeSvg"></div>
          <ng-template #qrPlaceholder>
            <div class="mfa-setup-qr-code-placeholder">
              <i class="fal fa-qrcode"></i>
            </div>
          </ng-template>
        </div>

        <!-- Text Code Toggle -->
        <ng-container *ngIf="totpSecret">
          <div>
            <button type="button"
                    class="btn btn--white btn--full btn--sm"
                    (click)="showTextCode = !showTextCode">
              <i class="fas mr-0050" [class.fa-chevron-down]="!showTextCode" [class.fa-chevron-up]="showTextCode"></i>
              Unable to scan? Enter code manually
            </button>
          </div>

          <!-- Text Code (Hidden by default) -->
          <div *ngIf="showTextCode" class="mfa-setup-text-code">
            <p class="txt-14 mb-0050 mt-0050">Enter this code in your authenticator app:</p>
            <div class="mfa-setup-text-code__value">
              <code>{{ totpSecret }}</code>
            </div>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Step 2: SMS Setup -->
    <div *ngIf="currentStepKey === 'setup-sms'" class="mb-0150 auth-card mat-card--simple mat-card--border">
      <div class="auth-card__header">
        <h2 class="auth-card__title">Setup 2-Factor Authentication</h2>
        <p class="auth-card__subtitle">Enter your phone number to receive a verification code via SMS.</p>
      </div>

      <div class="auth-card__content">
        <app-phone-field formControlName="phoneNumber"
                         label="Phone number"
                         campaignCountry="US"
                         countryFlagTooltip="Include the country code in your phone number."
                         fieldClass="mb-0025"></app-phone-field>

        <p class="txt-12 color--gray--005">
          Standard messaging rates apply. Country code is required.
        </p>
      </div>
    </div>

    <!-- Step 3: Shared Verification Code Entry -->
    <div *ngIf="currentStepKey === 'verify-code'" class="mb-0150 auth-card mat-card--simple mat-card--border">
      <div class="auth-card__header">
        <h2 class="auth-card__title">Enter Verification Code</h2>
        <ng-container *ngIf="isSmsFlow; else totpSubtitle">
          <p class="auth-card__subtitle">
            Enter the 6-digit code sent to your phone<ng-container *ngIf="maskedPhone"> ending in <strong>{{ maskedPhone }}</strong></ng-container>:
          </p>
        </ng-container>
        <ng-template #totpSubtitle>
          <p class="auth-card__subtitle">Enter the 6-digit code provided by your authenticator app:</p>
        </ng-template>
      </div>

      <div class="auth-card__content">
        <mat-form-field class="mb-0">
          <mat-label>Verification Code</mat-label>
          <input matInput
                type="text"
                formControlName="mfaToken"
                placeholder="123456"
                maxlength="6"
                pattern="[0-9]{6}"
                class="w-100"
                autocomplete="off">
          <app-input-error [formControlItem]="currentForm.get('mfaToken')"></app-input-error>
        </mat-form-field>

        <ng-container *ngIf="isSmsFlow">
          <div class="mt-0050">
            <p class="txt-meta-01 vert-0050">
              Didn't receive a code? <button type="button" class="link link--small pl-0" (click)="onResendCode()">Send again</button>
            </p>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Step 4: Setup Complete -->
    <div *ngIf="currentStepKey === 'setup-complete'" class="mb-0150 auth-card mat-card--simple mat-card--border">
      <div class="auth-card__header">
        <h2 class="auth-card__title">
          <i class="fas fa-shield-check color--green--002 mr-0025"></i>
          Setup Complete!
        </h2>
      </div>

      <div class="auth-card__content">
          Your account's additional verification has been successfully configured.
      </div>
    </div>

    <!-- Global Submit/Continue/Back Button -->
    <div class="mb-0100">
      <button type="submit"
            [disabled]="!canProceed"
            class="btn btn--full btn--lg btn--blue">
        {{ submitButtonText }}
      </button>
    </div>

    <div *ngIf="currentStepKey !== 'select-method' && currentStepKey !== 'setup-complete'">
      <button type="button"
              class="auth-back-btn"
              (click)="onBackToMethodSelection()">
        <i class="fal fa-arrow-left mr-0025"></i>
        Choose a different verification method
      </button>
    </div>
  </form>
</div>
